import { useId, useState } from 'react';
import PropTypes from 'prop-types';

import { ChevronDownIcon, ChevronRightOutlinedIcon, IconButton, ResetIcon } from '@utilities';

import TranslationRow from '../TranslationRow';

import styles from '../../_index.module.scss';

const getConceptName = (concept) => concept?.conceptName?.trim();

// Concepts translate on demand; otherwise their source names stay visible.
const ConceptTranslations = ({
  appliedLanguages,
  concepts,
  getEntry,
  getTranslation,
  isDisabled,
  languageLabels,
  onClear,
  onDismissRelevanceWarning,
  onReset,
  onTranslate,
  onTranslationChange,
  pendingTranslationKeys,
  sourceLanguage,
}) => {
  const contentId = useId();
  const toggleId = `${contentId}-toggle`;
  const conceptsWithNames = concepts.filter((concept) => getConceptName(concept));
  const [isExpanded, setIsExpanded] = useState(true);

  if (!conceptsWithNames.length) return null;

  return (
    <div className={styles['modal-translation-section']}>
      <div className={styles['modal-translation-section-header']}>
        <h3>
          <button
            aria-controls={contentId}
            aria-expanded={isExpanded}
            className={styles['modal-translation-section-toggle']}
            id={toggleId}
            onClick={() => setIsExpanded((expanded) => !expanded)}
            type="button"
          >
            <span className={styles['modal-translation-section-chevron']}>
              {isExpanded ? <ChevronDownIcon /> : <ChevronRightOutlinedIcon />}
            </span>
            <span>Concept Names</span>
          </button>
        </h3>
        {appliedLanguages.length > 0 && (
          <IconButton
            aria-label="Reset concept names"
            className={styles['modal-translation-reset']}
            isDisabled={isDisabled}
            onClick={onReset}
            tooltip="Reset"
            type="button"
          >
            <ResetIcon />
          </IconButton>
        )}
      </div>
      <div aria-labelledby={toggleId} hidden={!isExpanded} id={contentId} role="region">
        {conceptsWithNames.map(({ conceptName, id }) => (
          <TranslationRow
            appliedLanguages={appliedLanguages}
            entityKey={`concept-${id}`}
            fallbackToSource
            getEntry={getEntry}
            getTranslation={getTranslation}
            hasConceptName={false}
            isDisabled={isDisabled}
            isFormattingToolbarHidden
            key={id}
            languageLabels={languageLabels}
            onClear={() => onClear(`concept-${id}`)}
            onDismissRelevanceWarning={onDismissRelevanceWarning}
            onTranslate={(language) => onTranslate(id, language)}
            onTranslationChange={onTranslationChange}
            pendingTranslationKeys={pendingTranslationKeys}
            sourceLanguage={sourceLanguage}
            text={conceptName}
          />
        ))}
      </div>
    </div>
  );
};

ConceptTranslations.propTypes = {
  appliedLanguages: PropTypes.arrayOf(PropTypes.string).isRequired,
  concepts: PropTypes.array.isRequired,
  getEntry: PropTypes.func.isRequired,
  getTranslation: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  languageLabels: PropTypes.object.isRequired,
  onClear: PropTypes.func.isRequired,
  onDismissRelevanceWarning: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onTranslate: PropTypes.func.isRequired,
  onTranslationChange: PropTypes.func.isRequired,
  pendingTranslationKeys: PropTypes.instanceOf(Set),
  sourceLanguage: PropTypes.string,
};

export default ConceptTranslations;
